
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useUserProfile } from "@/hooks/useUserProfile";
import { UserProfileDetails } from "./UserProfileDetails";
import { Eye, EyeOff, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const UserInfo = () => {
  const { data: profile, isLoading, error } = useUserProfile();
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  if (error) {
    return <div>Erreur lors du chargement du profil</div>;
  }

  if (!profile) {
    return <div>Aucun profil trouvé</div>;
  }

  const handleUpdatePassword = async () => {
    if (!password) {
      toast.error("Veuillez entrer un mot de passe");
      return;
    }

    if (password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setIsUpdating(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      toast.success("Mot de passe modifié avec succès");
      setIsPasswordDialogOpen(false);
      setPassword("");
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast.error(error.message || "Erreur lors de la modification du mot de passe");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-4">
      <UserProfileDetails profile={profile} />
      
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="font-medium">Mot de passe:</label>
          <div className="flex items-center gap-2">
            <span className="font-mono">{showPassword ? password : "********"}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsPasswordDialogOpen(true)}
              className="flex items-center gap-2 h-8"
            >
              <Pencil className="h-4 w-4" />
              <span className="text-sm">Modifier</span>
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modification du mot de passe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nouveau mot de passe</Label>
              <div className="flex items-center gap-2">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="font-mono"
                  placeholder="Entrez votre nouveau mot de passe"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                  className="h-8 w-8 p-0"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <Button 
              onClick={handleUpdatePassword} 
              disabled={isUpdating}
              className="w-full"
            >
              {isUpdating ? "Modification..." : "Enregistrer le mot de passe"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
